import type { SupportedCountry } from "@App/utils/country";
import type { Locale } from "date-fns";
import { format, parseISO } from "date-fns";
import { de, deAT } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import { isHoliday, type Region } from "feiertagejs";

/**
 * These helpers are plain functions with roughly twenty call sites, so threading a
 * country argument through all of them would be disproportionate. The locale is held
 * module-locally and switched once when the country resolves — the same shape as
 * i18next's global `changeLanguage`, which the app already depends on.
 */
let activeLocale: Locale = de;

export const setDateLocale = (country: SupportedCountry): void => {
  activeLocale = country === "AT" ? deAT : de;
};

export const formatDate = (date: Date | string, formatString: string): string => {
  if (typeof date === "string") {
    date = parseISO(date);
  }
  return format(date, formatString, { locale: activeLocale });
};

export const formatShortDate = (date: Date | string): string => {
  if (typeof date === "string") {
    date = parseISO(date);
  }
  return format(date, "EEEEEE, dd.MM.", { locale: activeLocale });
};

export const toIsoString = (date: Date | string): string | null => {
  const d = typeof date === "string" ? parseISO(date) : date;

  if (isNaN(d.getTime())) {
    return null;
  }

  return formatInTimeZone(d, "Europe/Berlin", "yyyy-MM-dd'T'HH:mm:ssXXXXX", { locale: de });
};

export const isWeekend = (date: Date) => [0, 6].includes(date.getDay());
export const addBusinessDays = (date: Date, days: number, region: Region = "BUND") => {
  let count = 0;
  const newDate = new Date(date);
  while (count < days) {
    newDate.setDate(newDate.getDate() + 1);
    if (!isWeekend(newDate) && !isHoliday(newDate, region)) {
      count++;
    }
  }
  return newDate;
};
